import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { crmAPI } from '../services/api';
import './Settings.css';

const HUBFIT_API_URL = process.env.REACT_APP_API_URL || 'https://apidashboard.teamgreekgods.ma/api';

const Settings = () => {
  const { user, updateProfile } = useAuth();
  const [profile, setProfile] = useState({ name: '', role: '' });
  const [apiKey, setApiKey] = useState('');
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (user) {
      setProfile({ name: user.name || '', role: user.role || '' });
    }
    loadApiKey();
    loadGroups();
  }, [user]);

  const loadApiKey = async () => {
    try {
      const response = await crmAPI.getApiKey();
      if (response.data.success) {
        setApiKey(response.data.data || '');
      }
    } catch (error) {
      console.error('Failed to load API key', error);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await axios.get(`${HUBFIT_API_URL}/employees`, {
        headers: { Authorization: `Bearer ${process.env.REACT_APP_TOKEN}` }
      });
      if (response.data.success) {
        const employees = response.data.data || [];
        const formattedGroups = employees.map((emp) => ({
          id: emp.id,
          wa_number: emp.store_ids?.[0]?.name || `WA ${emp.store_ids?.[0]?.code || emp.store_id}`,
          name: `${emp.first_name} ${emp.last_name}`,
          is_active: emp.is_active === 1,
          phone: emp.professional_phone || emp.phone || ''
        }));
        setGroups(formattedGroups);
      }
    } catch (error) {
      console.error('Failed to load groups', error);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    await updateProfile(profile);
    setSaving(false);
  };

  const saveApiKey = async () => {
    setSaving(true);
    try {
      await crmAPI.saveApiKey(apiKey);
    } catch (error) {
      console.error('Failed to save API key', error);
    }
    setSaving(false);
  };

  const testApiKey = async () => {
    setTestResult(null);
    try {
      const token = localStorage.getItem('tgg_token');
      
      // Test stores endpoint
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/crm/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setTestResult({ success: true, message: `Connexion reussie - ${data.data.length} stores trouves` });
      } else {
        setTestResult({ success: false, message: data.message || 'Erreur de connexion' });
      }
    } catch (error) {
      setTestResult({ success: false, message: 'Erreur de connexion a l\'API' });
    }
  };

  return (
    <div className="settings-page">
      <div className="section-header">
        <div className="section-dot"></div>
        <h2>Parametres</h2>
      </div>

      {/* Profile */}
      <div className="settings-card">
        <div className="card-header">
          <div className="card-icon">&#9881;</div>
          <div>
            <div className="card-title">Profil</div>
            <div className="card-subtitle">Ton nom affiche dans la sidebar</div>
          </div>
        </div>
        <div className="card-body">
          <div className="form-row">
            <div className="form-group">
              <label>Nom</label>
              <input
                type="text"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Role</label>
              <input
                type="text"
                value={profile.role}
                onChange={(e) => setProfile({ ...profile, role: e.target.value })}
              />
            </div>
          </div>
          <button className="btn-primary" onClick={saveProfile} disabled={saving}>
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {/* API Key */}
      <div className="settings-card">
        <div className="card-header">
          <div className="card-icon">&#128273;</div>
          <div>
            <div className="card-title">API HubFit</div>
            <div className="card-subtitle">Token API de apidashboard.teamgreekgods.ma</div>
          </div>
        </div>
        <div className="card-body">
          <div className="form-group">
            <label>Cle API</label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Colle ta cle API HubFit ici"
            />
          </div>
          <div className="api-note">
            Token de apidashboard.teamgreekgods.ma - stocke uniquement sur le serveur.
          </div>
          <div className="btn-row">
            <button className="btn-primary" onClick={saveApiKey} disabled={saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            <button className="btn-secondary" onClick={testApiKey}>Tester</button>
          </div>
          {testResult && (
            <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
              {testResult.message}
            </div>
          )}
        </div>
      </div>

      {/* Employees */}
      <div className="settings-card">
        <div className="card-header">
          <div className="card-icon">&#128101;</div>
          <div>
            <div className="card-title">Employes</div>
            <div className="card-subtitle">Equipe depuis HubFit</div>
          </div>
          <button className="btn-secondary ml-auto" onClick={loadGroups}>
            &#8635; Actualiser
          </button>
        </div>
        <div className="card-body">
          <div className="employees-grid">
            {groups.map((emp) => (
              <div key={emp.id} className="employee-card">
                <div className="emp-avatar">
                  {emp.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div className="emp-info">
                  <div className="emp-name">{emp.name}</div>
                  <div className="emp-store">{emp.wa_number}</div>
                  <div className="emp-phone">{emp.phone || '-'}</div>
                </div>
                <span className={`emp-status ${emp.is_active ? 'active' : 'inactive'}`}>
                  {emp.is_active ? 'Actif' : 'Inactif'}
                </span>
              </div>
            ))}
          </div>
          {groups.length === 0 && (
            <div className="empty-state">
              <span className="empty-icon">&#128100;</span>
              <div className="empty-text">Aucun employe trouve</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
